/**
 * Discount & Spread Settings — Manage Capital's Segment 3. Per RAW investor code (not per
 * pool, since sql/44_): which rate Discounted Contract Balance discounts at, and Fair Value's
 * basis.
 *
 * Targets athena_bfsi_prc.prc_discount_spread_settings, re-keyed by
 * sql/44_prc_discount_spread_settings_by_code.sql from one row per pool to one row per raw
 * BankDwn investor code. investorCode/capitalType/facilityCode are now display-only — a code
 * maps to a pool through prc_facility_investor_code_map, and a code mapped to none or several
 * pools shows those fields null (facilityLabel falls back to "Code {bankdwnInvestorCode}").
 *
 * Direct write, no propose/approve, same reasoning as eligibility.ts. `fmvRate` is refreshed
 * from the live fair-value-spread grid on every read, keyed off each row's OWN fmvBasis — see
 * portfolio-risk-concentrations/app/router/discount_settings.py's module docstring.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getJson, patchJson, PRC_PREFIX } from "./client";

export type DcbRate = "OFF" | "CONTRACT_IRR" | "TREASURY_PLUS_SPREAD" | "SOFR_PLUS_SPREAD" | "FLAT";
export type FmvBasis = "OFF" | "TREASURY" | "SOFR" | "FLAT";

export interface DiscountSpreadSetting {
  settingId: string;
  bankdwnInvestorCode: string;
  investorCode: string | null;
  capitalType: "Debt" | "Equity" | null;
  facilityCode: string | null;
  facilityLabel: string;
  dcbRate: DcbRate;
  /** Read-only reference — the latest 5-year Treasury rate, never stored on this row. Shown
   *  beside the Spread input when dcbRate is TREASURY_PLUS_SPREAD. */
  currentTreasuryPct: number | null;
  currentTreasuryAsOf: string | null;
  /** A percent (8 = 8%), set by the user. Only meaningful when dcbRate is
   *  TREASURY_PLUS_SPREAD, but kept if set even when another rate is chosen — switching back
   *  should not lose the number. */
  treasurySpreadPct: number | null;
  /** Read-only reference — the latest overnight SOFR, never stored on this row. */
  currentSofrPct: number | null;
  currentSofrAsOf: string | null;
  sofrSpreadPct: number | null;
  /** A flat percent (8 = 8%) DCB discounts at when dcbRate is FLAT. */
  dcbFlatPct: number | null;
  /** A COUNT of priced fair-value-spread cells on THIS row's own fmvBasis — 0 for OFF/FLAT,
   *  which use no grid cell at all (not "not configured": OFF is an explicit, measured
   *  choice, per sql/44_). */
  fmvRate: number;
  fmvBasis: FmvBasis;
  /** A flat percent (8 = 8%) FMV discounts at when fmvBasis is FLAT. */
  fmvFlatPct: number | null;
  fmvVersion: number | null;
  fmvEffectiveDate: string | null;
  updatedAt: string | null;
}

/** The columns the table can be sorted by (the backend's own list). DCB Discount Rate sorts by the rate's name, then its number. */
export type DiscountSortKey = "investorCode" | "facility" | "dcbRate" | "fmvSpread";

/** One page of settings, sorted by `sort` across ALL rows (not just this page) when it is set; the table's own order otherwise. */
export function useDiscountSpreadSettings(page: {
  limit: number;
  offset: number;
  sort?: DiscountSortKey | null;
  dir?: "asc" | "desc";
}) {
  return useQuery({
    queryKey: ["prc-discount-spread-settings", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (page.sort) {
        q.set("sort", page.sort);
        q.set("dir", page.dir ?? "asc");
      }
      return getJson<{ settings: DiscountSpreadSetting[]; total: number }>(
        `${PRC_PREFIX}discount-spread-settings?${q.toString()}`,
      );
    },
  });
}

export function useUpdateDiscountSpreadSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      settingId: string;
      dcbRate?: DcbRate;
      treasurySpreadPct?: number | null;
      sofrSpreadPct?: number | null;
      dcbFlatPct?: number | null;
      fmvBasis?: FmvBasis;
      fmvFlatPct?: number | null;
    }) =>
      patchJson<{ ok: boolean }>(
        `${PRC_PREFIX}discount-spread-settings/${encodeURIComponent(input.settingId)}`,
        {
          dcbRate: input.dcbRate,
          treasurySpreadPct: input.treasurySpreadPct,
          sofrSpreadPct: input.sofrSpreadPct,
          dcbFlatPct: input.dcbFlatPct,
          fmvBasis: input.fmvBasis,
          fmvFlatPct: input.fmvFlatPct,
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prc-discount-spread-settings"] }),
  });
}

/** "20 cells configured · v3, eff. 2026-07-01" / "Off" / "8.00% flat". fmvRate is 0 (not
 *  null) for OFF and FLAT — both are measured, explicit choices, not "not configured". */
export function fmvSummaryLabel(s: DiscountSpreadSetting): string {
  if (s.fmvBasis === "OFF") return "Off";
  if (s.fmvBasis === "FLAT") {
    return s.fmvFlatPct === null ? "Flat" : `${s.fmvFlatPct.toFixed(2)}% flat`;
  }
  const version = s.fmvVersion ? ` · v${s.fmvVersion}` : "";
  const effective = s.fmvEffectiveDate ? `, eff. ${s.fmvEffectiveDate}` : "";
  return `${s.fmvRate} cell${s.fmvRate === 1 ? "" : "s"} configured${version}${effective}`;
}

/** The dropdown item label alone, no detail. */
export function dcbRateLabel(rate: DcbRate): string {
  switch (rate) {
    case "OFF": return "Off";
    case "CONTRACT_IRR": return "Contract IRR";
    case "TREASURY_PLUS_SPREAD": return "Current Treasury Rate + Spread";
    case "SOFR_PLUS_SPREAD": return "Current SOFR Rate + Spread";
    case "FLAT": return "Flat";
  }
}

/** Same as dcbRateLabel, but appends the chosen input for a table cell where it's on hand —
 *  "Current Treasury Rate + Spread — 4.83% + 1.50%", "Flat — 6.25%". */
export function dcbRateSummary(s: DiscountSpreadSetting): string {
  if (s.dcbRate === "TREASURY_PLUS_SPREAD" && s.currentTreasuryPct !== null && s.treasurySpreadPct !== null) {
    return `${dcbRateLabel(s.dcbRate)} — ${s.currentTreasuryPct.toFixed(2)}% + ${s.treasurySpreadPct.toFixed(2)}%`;
  }
  if (s.dcbRate === "SOFR_PLUS_SPREAD" && s.currentSofrPct !== null && s.sofrSpreadPct !== null) {
    return `${dcbRateLabel(s.dcbRate)} — ${s.currentSofrPct.toFixed(2)}% + ${s.sofrSpreadPct.toFixed(2)}%`;
  }
  if (s.dcbRate === "FLAT" && s.dcbFlatPct !== null) {
    return `${dcbRateLabel(s.dcbRate)} — ${s.dcbFlatPct.toFixed(2)}%`;
  }
  return dcbRateLabel(s.dcbRate);
}

export function fmvBasisLabel(basis: FmvBasis): string {
  switch (basis) {
    case "OFF": return "Off";
    case "TREASURY": return "Treasury";
    case "SOFR": return "SOFR";
    case "FLAT": return "Flat";
  }
}
